// src/defaultOptions.js

import { useContext, useEffect, useState, useRef } from "react";
import { MyContext, ListContext } from "./MyContext";

import posthog from "posthog-js";
import { calulateInitialCalendarDateRange } from "./calendarFunctions";
import Auth from "./Auth";
import { isOfflineError, isTimeoutError } from "./requestErrorUtils";

const REQUIRED_BOOTSTRAP_REQUEST_TIMEOUT_MS = 35000;
const BOOTSTRAP_TIMEOUT_MS = 45000;
const MAX_REQUIRED_BOOTSTRAP_RETRIES = 2;
const REQUIRED_BOOTSTRAP_RETRY_DELAY_MS = 1500;
const AUTH_READY_TIMEOUT_MS = 15000;

const DefaultOptions = () => {
  const { mystate, dispatch } = useContext(MyContext);
  const { dispatch2 } = useContext(ListContext);

  const [authReady, setAuthReady] = useState(false);
  const [bootstrapTimedOut, setBootstrapTimedOut] = useState(false);
  const [bootstrapFailed, setBootstrapFailed] = useState(false);
  const [bootstrapErrorMessage, setBootstrapErrorMessage] = useState("");
  const [bootstrapAttempt, setBootstrapAttempt] = useState(0);
  const [bootstrapFailureKind, setBootstrapFailureKind] = useState("");
  const [authInitAttempt, setAuthInitAttempt] = useState(0);

  const captureBootstrapStage = (stage, extra = {}) => {
    const payload = {
      stage,
      bootstrapAttempt,
      authInitAttempt,
      online: typeof navigator !== "undefined" ? navigator.onLine : undefined,
      ...extra,
    };

    // Keep this lightweight so it can stay enabled in production triage.
    console.log("[bootstrap]", payload);
    posthog?.capture?.("bootstrap_stage", payload);
  };

  // Bootstrap flow summary:
  // 1) Success: required fetches complete -> SET_DEFAULTS_LOADED.
  // 2) Auth failure (missing token/401): reset state and redirect to /login.
  // 3) Timeout: mark timed out + failed and render timeout guidance.
  // 4) Offline/other required fetch error: mark failed and render message.

  // Wait for Auth.authReady once, then set local state
  useEffect(() => {
    let cancelled = false;
    let timeoutId;

    captureBootstrapStage("auth_ready_wait_start");

    const checkAuth = async () => {
      try {
        await Promise.race([
          Auth.authReady,
          new Promise((_, reject) => {
            timeoutId = setTimeout(
              () => reject(new Error("auth_ready_timeout")),
              AUTH_READY_TIMEOUT_MS,
            );
          }),
        ]);

        if (cancelled) return;
        clearTimeout(timeoutId);
        setAuthReady(true);
        captureBootstrapStage("auth_ready_resolved");
      } catch (error) {
        if (cancelled) return;

        setBootstrapTimedOut(true);
        setBootstrapFailed(true);
        setBootstrapFailureKind("auth");
        setBootstrapErrorMessage(
          "Login succeeded but startup could not finish. Please retry or go to login.",
        );
        captureBootstrapStage("auth_ready_timeout");
      }
    };

    checkAuth();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [authInitAttempt]);

  const redirecting = useRef(false);

  const handleAuthError = () => {
    if (redirecting.current) return; // prevent 5 simultaneous redirects
    redirecting.current = true;
    Auth.setToken("");
    dispatch({ type: "RESET_STATE" });
    dispatch2(["RESET_STATE"]);
    posthog?.reset();
    window.location.href = "/login";
  };

  const forceLoginRecovery = () => {
    handleAuthError();
  };

  // Required bootstrap data for the admin shell.
  const [requiredDone, setRequiredDone] = useState({
    farm: false,
    permissions: false,
  });

  // Optional convenience data for filters and select boxes.
  const [optionalDone, setOptionalDone] = useState({ options: false });

  const areRequiredFetchesDone = requiredDone.farm && requiredDone.permissions;
  const areOptionalFetchesDone = optionalDone.options;

  const retryRequiredBootstrap = () => {
    captureBootstrapStage("manual_retry_clicked");
    setBootstrapFailed(false);
    setBootstrapTimedOut(false);
    setBootstrapErrorMessage("");
    setBootstrapFailureKind("");
    setRequiredDone({ farm: false, permissions: false });
    setBootstrapAttempt(0);
    setAuthReady(false);
    setAuthInitAttempt((prev) => prev + 1);
  };

  // Required bootstrap data for the admin shell.
  useEffect(() => {
    if (!authReady) return;
    let cancelled = false;
    let retryTimerId;

    captureBootstrapStage("required_bootstrap_cycle_start");

    setBootstrapFailed(false);
    setBootstrapTimedOut(false);
    setBootstrapErrorMessage("");
    setBootstrapFailureKind("");

    const fetchRequiredBootstrap = async () => {
      try {
        if (!Auth.getToken()) {
          captureBootstrapStage("required_bootstrap_missing_token");
          handleAuthError();
          return;
        }

        captureBootstrapStage("required_bootstrap_request_start", {
          endpoint: "farms/farmDefaults",
        });

        const defaults = await Auth.api("farms/farmDefaults", "get", null, {
          skipGlobalErrorHandler: true,
          timeout: REQUIRED_BOOTSTRAP_REQUEST_TIMEOUT_MS,
        });

        if (cancelled) return;

        if (!defaults?.data?.farmDefaults) {
          handleAuthError();
          return;
        }

        dispatch({
          type: "SET_FARM_DEFAULTS",
          data: defaults.data.farmDefaults,
          currentUser: defaults.data.currentUser || "",
        });
        dispatch({
          type: "SET_SHOW_BY_STUDENT_NAME",
          data:
            Auth.getUserType() == "STU"
              ? false
              : defaults.data.farmDefaults.show_lesson_by_client_name,
        });

        dispatch2([
          "SET_SHOW_BY_STUDENT_NAME",
          Auth.getUserType() == "STU"
            ? false
            : defaults.data.farmDefaults.show_lesson_by_client_name,
        ]);

        dispatch({
          type: "SET_VIEW",
          data: defaults.data.farmDefaults.default_calendar_view,
        });
        const [currentStart, currentEnd] = calulateInitialCalendarDateRange(
          defaults.data.farmDefaults.default_calendar_view,
        );
        dispatch({
          type: "SET_CALENDAR_DATE_RANGE",
          start: currentStart,
          end: currentEnd,
        });
        dispatch({
          type: "SET_RESOURCE",
          data: defaults.data.farmDefaults.default_day_view,
        });
        dispatch({
          type: "SET_RESOURCE_ACCESSOR",
          resourceAccessor: `${defaults.data.farmDefaults.default_day_view}_id`,
        });

        dispatch2(["SET_USER_PERMISSIONS", defaults.data.permissions || []]);

        setRequiredDone({ farm: true, permissions: true });
        setBootstrapFailureKind("");
        captureBootstrapStage("required_bootstrap_request_success");
      } catch (error) {
        if (error.response?.status === 401) return handleAuthError();

        const canRetry =
          bootstrapAttempt < MAX_REQUIRED_BOOTSTRAP_RETRIES &&
          (isTimeoutError(error) ||
            isOfflineError(error) ||
            !error?.response);

        if (canRetry) {
          captureBootstrapStage("required_bootstrap_retry_scheduled", {
            reason: isTimeoutError(error)
              ? "timeout"
              : isOfflineError(error)
                ? "offline"
                : "no_response",
          });
          retryTimerId = setTimeout(() => {
            if (cancelled) return;
            setBootstrapAttempt((prev) => prev + 1);
          }, REQUIRED_BOOTSTRAP_RETRY_DELAY_MS);
          return;
        }

        if (isTimeoutError(error)) {
          setBootstrapTimedOut(true);
          setBootstrapFailed(true);
          setBootstrapFailureKind("timeout");
          setBootstrapErrorMessage(
            "Loading farm settings timed out. Please refresh or try again later.",
          );
          captureBootstrapStage("required_bootstrap_failed_timeout");
          return;
        }

        if (isOfflineError(error)) {
          setBootstrapFailed(true);
          setBootstrapFailureKind("offline");
          setBootstrapErrorMessage(
            "We could not load farm settings because your connection was interrupted. Please reconnect and try again.",
          );
          captureBootstrapStage("required_bootstrap_failed_offline");
          return;
        }

        setBootstrapFailed(true);
        setBootstrapFailureKind("other");
        setBootstrapErrorMessage(
          "Defaults or permissions failed to load. Please refresh or try again later.",
        );
        captureBootstrapStage("required_bootstrap_failed_other", {
          status: error?.response?.status,
          message: error?.message,
        });
      }
    };

    fetchRequiredBootstrap();

    return () => {
      cancelled = true;
      clearTimeout(retryTimerId);
    };
    //eslint-disable-next-line
  }, [authReady, bootstrapAttempt]);

  // OPTIONS - load after required bootstrap to reduce startup request contention.
  useEffect(() => {
    if (!authReady || !areRequiredFetchesDone) return;

    const fetchData = async () => {
      let params = {};
      try {
        const result = await Auth.api("options/index", "get", params, {
          skipGlobalErrorHandler: true,
        });
        dispatch({
          type: "UPDATE_OPTIONS",
          options: result.data,
        });
        dispatch({
          type: "SET_INITAL_SECTION_DEFAULTS",
          data: result.data.defaultSection,
        });
        setOptionalDone((prev) => ({ ...prev, options: true }));
      } catch (error) {
        if (error.response?.status === 401) return handleAuthError();
        setOptionalDone((prev) => ({ ...prev, options: true })); // non-auth failure, keep going
      }
    };
    fetchData();
    //eslint-disable-next-line
  }, [authReady, areRequiredFetchesDone]);

  // Only set defaultsLoaded after all fetches are done
  useEffect(() => {
    if (areRequiredFetchesDone) {
      dispatch({ type: "SET_DEFAULTS_LOADED" });
    }
  }, [areRequiredFetchesDone, dispatch]);

  // Ensure a single hung required request cannot block app bootstrap forever.
  useEffect(() => {
    if (!authReady || areRequiredFetchesDone || bootstrapFailed) return;

    const timeoutId = setTimeout(() => {
      setBootstrapTimedOut(true);
      setBootstrapFailed(true);
      setBootstrapErrorMessage(
        "Farm defaults or permissions timed out. Please refresh or try again later.",
      );
      captureBootstrapStage("bootstrap_watchdog_timeout");
    }, BOOTSTRAP_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [authReady, areRequiredFetchesDone, bootstrapFailed]);

  useEffect(() => {
    if (!bootstrapFailed || bootstrapFailureKind !== "offline") return;

    const onReconnect = () => {
      captureBootstrapStage("network_reconnected_retry");
      retryRequiredBootstrap();
    };

    window.addEventListener("online", onReconnect);
    return () => window.removeEventListener("online", onReconnect);
  }, [bootstrapFailed, bootstrapFailureKind]);

  if (bootstrapFailed) {
    return (
      <div
        style={{
          width: "100%",
          minHeight: "35vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 18, marginBottom: 8 }}>
          Unable to load settings
        </div>
        <div style={{ opacity: 0.85, maxWidth: 620 }}>
          {bootstrapErrorMessage}
        </div>
        {bootstrapTimedOut && (
          <div style={{ marginTop: 12, color: "#b15b00", maxWidth: 580 }}>
            The startup request timed out.
          </div>
        )}
        <button
          type="button"
          onClick={retryRequiredBootstrap}
          style={{
            marginTop: 16,
            padding: "8px 14px",
            borderRadius: 6,
            border: "1px solid #aaa",
            background: "#fff",
            cursor: "pointer",
          }}
        >
          Retry loading settings
        </button>
        <button
          type="button"
          onClick={forceLoginRecovery}
          style={{
            marginTop: 10,
            padding: "8px 14px",
            borderRadius: 6,
            border: "1px solid #bbb",
            background: "#f8f8f8",
            cursor: "pointer",
          }}
        >
          Go to login
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        minHeight: "35vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 18, marginBottom: 8 }}>
        Loading farm settings...
      </div>
      <div style={{ opacity: 0.8, maxWidth: 520 }}>
        Please wait while we load your required settings.
      </div>
      {bootstrapAttempt > 0 && (
        <div style={{ marginTop: 10, opacity: 0.7, maxWidth: 580 }}>
          Retrying startup request ({bootstrapAttempt}/
          {MAX_REQUIRED_BOOTSTRAP_RETRIES})...
        </div>
      )}
      {!areRequiredFetchesDone && bootstrapTimedOut && (
        <div style={{ marginTop: 12, color: "#b15b00", maxWidth: 580 }}>
          Some startup requests took too long.
        </div>
      )}
      {areRequiredFetchesDone && !areOptionalFetchesDone && (
        <div style={{ marginTop: 12, opacity: 0.7, maxWidth: 580 }}>
          Core settings are ready. Loading optional dropdown data in the
          background.
        </div>
      )}
    </div>
  );
};

export default DefaultOptions;
